"use client";

import { useEffect, useRef, useState } from "react";

import { cn } from "@/lib/utils";

type Props = {
  title: string;
  cover?: string;
  clip?: string;
  clipWebm?: string;
  priority?: boolean;
  /** Text shown in the window title bar. Omit for no title. */
  label?: string;
  size?: "sm" | "lg";
  className?: string;
};

export function ProjectMedia({ title, cover, clip, clipWebm, priority, label, size = "lg", className }: Props) {
  const video = useRef<HTMLVideoElement>(null);
  const [reduce, setReduce] = useState(false);

  useEffect(() => {
    setReduce(window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  }, []);

  useEffect(() => {
    const el = video.current;
    if (!el || reduce) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) el.play().catch(() => {});
        else el.pause();
      },
      { threshold: 0.35 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [reduce]);

  if (!cover && !clip) return null;

  const sm = size === "sm";

  // A thin window frame: title bar with traffic-light dots, 1px hairlines, fixed 16:10 media box.
  return (
    <figure
      className={cn(
        "m-0 overflow-hidden border bg-surface-2",
        sm ? "rounded-lg" : "rounded-xl shadow-(--shadow-card)",
        className,
      )}
    >
      <div
        className={cn(
          "relative flex items-center border-b border-hairline",
          sm ? "h-6 px-2.5" : "h-8 px-3.5",
        )}
        aria-hidden="true"
      >
        <span className="flex gap-1.5">
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className={cn("rounded-full bg-foreground/12", sm ? "size-[7px]" : "size-2.5")}
            />
          ))}
        </span>
        {label && (
          <span
            className={cn(
              "absolute inset-x-16 truncate text-center font-medium text-muted-foreground",
              sm ? "text-[10px]" : "text-xs",
            )}
          >
            {label}
          </span>
        )}
      </div>
      {clip ? (
        <video
          ref={video}
          className="block aspect-[16/10] w-full bg-muted object-cover"
          poster={cover}
          muted
          loop
          playsInline
          preload={priority ? "auto" : "none"}
          controls={reduce}
          aria-label={`${title}, screen recording`}
        >
          {clipWebm && <source src={clipWebm} type="video/webm" />}
          <source src={clip} type="video/mp4" />
        </video>
      ) : (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={cover}
          alt={`${title}, screenshot`}
          className="block aspect-[16/10] w-full bg-muted object-cover"
          loading={priority ? "eager" : "lazy"}
          decoding="async"
        />
      )}
    </figure>
  );
}
